import React, { FC, useMemo, useState } from 'react';
import { Avatar, Button, Select, TextLink } from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import { useMutation, useQuery } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import { queryKey } from 'constants/queryKey';
import { getUsersWithMobileApp } from 'app/modules/posts/api/getUsersWithMobileApp';
import { changeUserToRetrieveSchedulePushNotification } from 'app/modules/posts/api/changeUserToRetrieveSchedulePushNotification';
import Logger from 'utils/logger';
import { PageUserWithMobileApp } from 'types/User';
import { ApiClientError, queryClient } from 'api/client';
import { useTranslation } from 'react-i18next';
import { useModalHistory } from 'context/ModalRouter';
import { modalRoutes } from 'constants/routes';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';

type Props = {
  pageId: number;
  postId: number;
  open: boolean;
  onClose: () => void;
  selectedUserId?: number;
};

type Optionprops = {
  label: string;
  src: string;
};

const SelectOption: FC<Optionprops> = (props) => (
  <div className="tw-flex tw-w-full tw-gap-2 tw-px-2">
    <Avatar src={props.src} />
    <span>{props.label}</span>
  </div>
);

const formatUserForSelectValue = (user: PageUserWithMobileApp) => {
  return {
    value: user.id,
    label: user.name,
    avatar: user.avatar,
  };
};

const ChangeUserToRetrieveSchedulePushNotificationModal: FC<Props> = (
  props,
) => {
  const [userId, setUserId] = useState<number | undefined>(
    props.selectedUserId,
  );
  const toast = useToast();
  const { t } = useTranslation();
  const modalHistory = useModalHistory();

  const { data, isLoading } = useQuery(
    queryKey.pageUsersWithMobileApp(props.pageId),
    () => getUsersWithMobileApp(props.pageId),
  );

  const changeUserMutation = useMutation(
    (id: number) =>
      changeUserToRetrieveSchedulePushNotification({
        userId: id,
        pageId: props.pageId,
        postId: props.postId,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries(queryKey.postDetail(props.postId));
        props.onClose();
        toast(t('userHasBeenChanged'), 'success');
      },

      onError(e: ApiClientError) {
        toast(e?.userMessage ?? t('somethingWentWrongTryAgain'), 'error');
        Logger.error(e);
      },
    },
  );

  function requestToChangeUser() {
    if (userId) {
      changeUserMutation.mutate(userId);
    }
  }

  function openInviteUser() {
    props.onClose();
    modalHistory.push({
      hash: modalRoutes.INVITE_USER,
      search: `?profileId=${props.pageId}`,
    });
  }

  const preSelectedUser = useMemo(() => {
    const user =
      data?.find((user) => user.id === props.selectedUserId) ?? data?.[0];
    return user ? formatUserForSelectValue(user) : null;
  }, [data, props.selectedUserId]);

  return (
    <Modal
      classNames={{
        modal: '!tw-w-[400px]',
      }}
      open={props.open}
      onClose={props.onClose}
    >
      <Modal.Header title={t('user')} />
      <Modal.Content className="tw-gap-1 tw-overflow-visible tw-py-2">
        <Select
          defaultValue={preSelectedUser}
          isLoading={isLoading}
          options={data?.map(formatUserForSelectValue)}
          placeholder={t('changeUser')}
          components={{
            SingleValue: (option) => (
              <SelectOption
                label={option.data.label}
                src={option.data.avatar}
              />
            ),
          }}
          formatOptionLabel={(option) => (
            <SelectOption label={option.label} src={option.avatar} />
          )}
          onChange={(option) => setUserId(option?.value)}
          isSearchable={false}
          isDisabled={!data?.length}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (styles) => ({
              ...styles,
              zIndex: 1010,
            }),
            container: (css) => ({
              ...css,
              zIndex: 20,
            }),
            valueContainer: (css) => ({
              ...css,
              paddingLeft: '0',
              paddingRight: '0',
              input: {
                position: 'absolute',
              },
            }),
          }}
        />
        <UserPermissionGate scopes={UserPermission.USER_CREATE}>
          <TextLink>
            <Button
              variant="plain"
              size="small"
              className="tw-mt-1"
              onClick={openInviteUser}
            >
              {t('inviteTeamMember')}
            </Button>
          </TextLink>
        </UserPermissionGate>
      </Modal.Content>
      <Modal.Footer withDivider>
        <Button
          className="tw-w-full"
          onClick={requestToChangeUser}
          disabled={!userId}
          isLoading={changeUserMutation.isLoading}
        >
          {t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default ChangeUserToRetrieveSchedulePushNotificationModal;
